import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import type { League } from '../../shared/types';
import UpgradeBanner from '../components/UpgradeBanner';

function isPlanLimit(msg: string) { return msg.startsWith('Free plan'); }

const API_URL = import.meta.env.VITE_API_URL as string;

export default function Leagues() {
  const { getToken, logout } = useAuth();
  const navigate = useNavigate();

  const [leagues,  setLeagues]  = useState<League[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');

  // Create league modal
  const [showCreate,    setShowCreate]    = useState(false);
  const [newName,       setNewName]       = useState('');
  const [newMaxPlayers, setNewMaxPlayers] = useState<number | ''>(32);
  const [newCourts,     setNewCourts]     = useState<number | ''>(4);
  const [creating,      setCreating]      = useState(false);
  const [createError,   setCreateError]   = useState('');

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res   = await fetch(`${API_URL}/leagues`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load leagues');
        const data = await res.json() as { leagues: League[] };
        setLeagues(data.leagues);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load leagues');
      } finally {
        setLoading(false);
      }
    })();
  }, [getToken, navigate]);

  async function createLeague(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreateError('');
    setCreating(true);
    try {
      const token = await getToken();
      const res   = await fetch(`${API_URL}/leagues`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ name: newName.trim(), maxPlayers: Number(newMaxPlayers) || 32, courts: Number(newCourts) || 4 }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(data.error ?? 'Failed to create league');
      }
      const data = await res.json() as { id: string };
      navigate(`/leagues/${data.id}/dashboard`);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create league');
      setCreating(false);
    }
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'var(--gray-500)' }}>Loading…</p>
      </div>
    );
  }

  const active   = leagues.filter(l => l.status !== 'archived');
  const archived = leagues.filter(l => l.status === 'archived');

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '32px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>My Leagues</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
            + New League
          </button>
          <button className="btn btn-secondary btn-sm" onClick={logout}>Sign out</button>
        </div>
      </div>

      {error && <div className="alert alert-red">{error}</div>}

      {active.length === 0 && !loading && (
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <p style={{ color: 'var(--gray-500)', marginBottom: 16 }}>No active leagues yet.</p>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            Create your first league
          </button>
        </div>
      )}

      {active.map(league => (
        <div
          key={league.id}
          className="card"
          style={{ cursor: 'pointer', marginBottom: 12 }}
          onClick={() => navigate(`/leagues/${league.id}/dashboard`)}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1rem' }}>{league.name}</div>
              <div style={{ fontSize: '.8rem', color: 'var(--gray-500)', marginTop: 2 }}>
                {league.maxPlayers} players · {league.courts} courts
              </div>
            </div>
            <span style={{
              background: 'var(--green)', color: '#fff',
              borderRadius: 4, padding: '2px 8px', fontSize: '.75rem', fontWeight: 600,
            }}>
              Active
            </span>
          </div>
        </div>
      ))}

      {archived.length > 0 && (
        <details style={{ marginTop: 24 }}>
          <summary style={{ cursor: 'pointer', color: 'var(--gray-500)', fontSize: '.875rem', marginBottom: 12 }}>
            Archived leagues ({archived.length})
          </summary>
          {archived.map(league => (
            <div
              key={league.id}
              className="card"
              style={{ opacity: 0.6, cursor: 'pointer', marginBottom: 8 }}
              onClick={() => navigate(`/leagues/${league.id}/dashboard`)}
            >
              <div style={{ fontWeight: 600 }}>{league.name}</div>
              <div style={{ fontSize: '.8rem', color: 'var(--gray-500)' }}>Archived</div>
            </div>
          ))}
        </details>
      )}

      {/* Create league modal */}
      {showCreate && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: 20,
        }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 460, width: '100%' }}>
            <h3 style={{ fontWeight: 800, marginBottom: 20 }}>New League</h3>

            <form onSubmit={createLeague} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label>League Name *</label>
                <input
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="e.g. Tuesday Night Pickles"
                  autoFocus
                  required
                  style={{ marginTop: 4 }}
                />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label>Max Players</label>
                  <input
                    type="number" min={4} max={500}
                    value={newMaxPlayers}
                    onChange={e => setNewMaxPlayers(e.target.value === '' ? '' : parseInt(e.target.value))}
                  />
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label>Default Courts</label>
                  <input
                    type="number" min={1} max={20}
                    value={newCourts}
                    onChange={e => setNewCourts(e.target.value === '' ? '' : parseInt(e.target.value))}
                  />
                </div>
              </div>

              {createError && isPlanLimit(createError)
                ? <UpgradeBanner message={createError} />
                : createError && <div className="alert alert-red">{createError}</div>
              }

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { setShowCreate(false); setNewName(''); setCreateError(''); }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? 'Creating…' : 'Create League'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
